import React, { useState } from 'react';
import { 
  Package, 
  Wrench,
  Search,
  Filter,
  Plus,
  Edit,
  AlertTriangle,
  CheckCircle,
  Eye,
  Calendar,
  MapPin,
  User,
  BarChart3,
  Settings
} from 'lucide-react';
import Card from '../../components/Card';

const InstallationTools = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Inventar unelte și echipamente
  const tools = [
    {
      id: 'TOOL-001',
      name: 'Boormașină Profesională Makita',
      category: 'electric',
      status: 'available',
      condition: 'excellent',
      location: 'Depozit Central',
      assignedTo: null,
      price: 450,
      purchaseDate: '2024-03-15',
      lastMaintenance: '2024-12-01',
      nextMaintenance: '2025-03-01',
      specifications: {
        power: '800W',
        maxDrill: '13mm',
        battery: 'Li-Ion 18V',
        weight: '1.8kg'
      },
      accessories: ['Set burghie', 'Valiză transport', 'Baterie extra'],
      usage: 156,
      maxUsage: 2000
    },
    {
      id: 'TOOL-002',
      name: 'Nivelă Laser Bosch',
      category: 'measuring',
      status: 'in_use',
      condition: 'good',
      location: 'Echipa Ștefan',
      assignedTo: 'Ștefan Georgescu',
      price: 320,
      purchaseDate: '2024-01-20',
      lastMaintenance: '2024-11-15',
      nextMaintenance: '2025-02-15',
      specifications: {
        range: '20m',
        accuracy: '±0.5mm/m',
        laserClass: 'Class 2',
        battery: '4xAA'
      },
      accessories: ['Trepied', 'Ochelari protecție', 'Telecomandă'],
      usage: 89,
      maxUsage: 1500
    },
    {
      id: 'TOOL-003',
      name: 'Kit Etanșare Premium',
      category: 'consumables',
      status: 'available',
      condition: 'new',
      location: 'Depozit Central',
      assignedTo: null,
      price: 85,
      purchaseDate: '2024-12-20',
      lastMaintenance: null,
      nextMaintenance: null,
      specifications: {
        type: 'Silicone & Polyuretan',
        colors: '5 culori',
        temperature: '-40°C to +150°C',
        quantity: '10 tuburi'
      },
      accessories: ['Pistol etanșare', 'Spatulă', 'Solventi'],
      usage: 0,
      maxUsage: 50
    },
    {
      id: 'TOOL-004',
      name: 'Mașină de frezat Festool',
      category: 'electric',
      status: 'maintenance',
      condition: 'fair',
      location: 'Service External',
      assignedTo: null,
      price: 680,
      purchaseDate: '2023-06-10',
      lastMaintenance: '2024-12-20',
      nextMaintenance: '2025-01-15',
      specifications: {
        power: '1200W',
        speed: '10000-22000 rpm',
        depth: '60mm',
        weight: '3.2kg'
      },
      accessories: ['Set freze', 'Ghidaj paralel', 'Aspirator'],
      usage: 445,
      maxUsage: 3000,
      maintenanceReason: 'Înlocuire perii carbon'
    },
    {
      id: 'TOOL-005',
      name: 'Burghie Speciale Ferestre',
      category: 'consumables',
      status: 'low_stock',
      condition: 'good',
      location: 'Echipa Eusebiu',
      assignedTo: 'Eusebiu Marinescu',
      price: 145,
      purchaseDate: '2024-09-05',
      lastMaintenance: null,
      nextMaintenance: null,
      specifications: {
        sizes: '4-16mm',
        material: 'HSS Co',
        coating: 'TiN',
        quantity: '15 bucăți'
      },
      accessories: ['Cutie organizare', 'Marcator măsuri'],
      usage: 38,
      maxUsage: 45,
      stockAlert: true
    },
    {
      id: 'TOOL-006',
      name: 'Nivela cu Bule Stanley',
      category: 'measuring',
      status: 'available',
      condition: 'excellent',
      location: 'Depozit Central',
      assignedTo: null,
      price: 65,
      purchaseDate: '2024-02-28',
      lastMaintenance: null,
      nextMaintenance: '2025-08-28',
      specifications: {
        length: '60cm',
        accuracy: '0.5mm/m',
        material: 'Aluminiu',
        bubbles: '3'
      },
      accessories: ['Husă protecție'],
      usage: 42,
      maxUsage: 800
    },
    {
      id: 'TOOL-007',
      name: 'Set Încuietori Universale',
      category: 'hardware',
      status: 'available',
      condition: 'new',
      location: 'Depozit Central',
      assignedTo: null,
      price: 230,
      purchaseDate: '2024-11-30',
      lastMaintenance: null,
      nextMaintenance: null,
      specifications: {
        types: 'Cilindru, yale, securitate',
        quantity: '12 bucăți',
        security: 'Clasa 4',
        keys: '36 chei'
      },
      accessories: ['Cheie calibrare', 'Ulei menținere'],
      usage: 2,
      maxUsage: 100
    },
    {
      id: 'TOOL-008',
      name: 'Aspirator Industrial Karcher',
      category: 'cleaning',
      status: 'in_use',
      condition: 'good',
      location: 'Echipa Support',
      assignedTo: 'Rotativ',
      price: 395,
      purchaseDate: '2024-04-12',
      lastMaintenance: '2024-10-15',
      nextMaintenance: '2025-04-15',
      specifications: {
        power: '1200W',
        capacity: '30L',
        suction: '248 mbar',
        noise: '69 dB'
      },
      accessories: ['Set furtunuri', 'Perii speciale', 'Filtre HEPA'],
      usage: 78,
      maxUsage: 1200
    }
  ];

  const categories = [
    { id: 'all', name: 'Toate', icon: Package },
    { id: 'electric', name: 'Electrice', icon: Wrench },
    { id: 'measuring', name: 'Măsurare', icon: BarChart3 },
    { id: 'consumables', name: 'Consumabile', icon: Package },
    { id: 'hardware', name: 'Hardware', icon: Settings },
    { id: 'cleaning', name: 'Curățenie', icon: CheckCircle }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_use': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'maintenance': return 'bg-red-100 text-red-800 border-red-200';
      case 'low_stock': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available': return 'Disponibil';
      case 'in_use': return 'În Folosință';
      case 'maintenance': return 'Service';
      case 'low_stock': return 'Stoc Scăzut';
      default: return 'Necunoscut';
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      case 'new': return 'text-primary-600';
      default: return 'text-gray-600';
    }
  };

  const getConditionText = (condition) => {
    switch (condition) {
      case 'excellent': return 'Excelentă';
      case 'good': return 'Bună';
      case 'fair': return 'Acceptabilă';
      case 'poor': return 'Slabă';
      case 'new': return 'Nouă';
      default: return 'N/A';
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.icon : Package;
  };

  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (tool.assignedTo && tool.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Statistics
  const totalTools = tools.length;
  const availableTools = tools.filter(t => t.status === 'available').length;
  const inUseTools = tools.filter(t => t.status === 'in_use').length;
  const maintenanceTools = tools.filter(t => t.status === 'maintenance').length;
  const lowStockTools = tools.filter(t => t.status === 'low_stock').length;
  const totalValue = tools.reduce((sum, tool) => sum + tool.price, 0);

  const ToolModal = ({ tool, onClose }) => {
    if (!tool) return null;
    
    const CategoryIcon = getCategoryIcon(tool.category);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl m-4 max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CategoryIcon className="w-6 h-6 text-primary-600" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {tool.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">ID: {tool.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Informații Generale
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Categorie:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {categories.find(c => c.id === tool.category)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(tool.status)}`}>
                      {getStatusText(tool.status)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Stare:</span>
                    <span className={`font-medium ${getConditionColor(tool.condition)}`}>
                      {getConditionText(tool.condition)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Locație:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tool.location}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Alocat la:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tool.assignedTo || 'Nealocat'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Specificații Tehnice
                </h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(tool.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Financiar și Utilizare
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Preț achiziție:</span>
                    <span className="font-bold text-primary-600">
                      {tool.price.toLocaleString('ro-RO')} €
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Data achiziție:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(tool.purchaseDate).toLocaleDateString('ro-RO')}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">Utilizare:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {tool.usage} / {tool.maxUsage}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(tool.usage / tool.maxUsage) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Mentenanță
                </h4>
                <div className="space-y-2 text-sm">
                  {tool.lastMaintenance && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Ultima:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(tool.lastMaintenance).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                  )}
                  {tool.nextMaintenance && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Următoarea:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(tool.nextMaintenance).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                  )}
                  {tool.maintenanceReason && (
                    <div className="mt-2">
                      <span className="text-gray-600 dark:text-gray-300">Motiv service:</span>
                      <p className="text-red-600 font-medium mt-1">
                        {tool.maintenanceReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Accesorii Incluse
                </h4>
                <div className="space-y-1">
                  {tool.accessories.map((accessory, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <CheckCircle className="w-3 h-3 text-green-600 mr-2" />
                      <span className="text-gray-600 dark:text-gray-300">{accessory}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Închide
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Editează Unelta
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Package className="w-8 h-8 text-secondary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Inventar Unelte
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Management echipamente și unelte de instalare TIBECO
            </p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors">
          <Plus className="w-4 h-4" />
          Adaugă Uneltă
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Caută unelte, categorii, utilizatori..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {totalTools}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Total Unelte
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {availableTools}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Disponibile
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {inUseTools}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              În Folosință
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {maintenanceTools}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Service
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {lowStockTools}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Stoc Scăzut
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-soft">
          <div className="text-center">
            <div className="text-lg font-bold text-primary-600">
              {totalValue.toLocaleString('ro-RO')}€
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Valoare Totală
            </div>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTools.map((tool) => {
          const CategoryIcon = getCategoryIcon(tool.category);
          
          return (
            <Card key={tool.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Tool Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-secondary-400 to-primary-400 rounded-full flex items-center justify-center">
                      <CategoryIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {tool.name}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {tool.id}
                      </p>
                    </div>
                  </div>
                  {tool.stockAlert && (
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  )}
                </div>

                {/* Status and Condition */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(tool.status)}`}>
                    {getStatusText(tool.status)}
                  </span>
                  <span className={`text-xs font-medium ${getConditionColor(tool.condition)}`}>
                    {getConditionText(tool.condition)}
                  </span>
                </div>

                {/* Tool Info */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Locație:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tool.location}
                    </span>
                  </div>
                  {tool.assignedTo && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Alocat la:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {tool.assignedTo}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Preț:</span>
                    <span className="font-bold text-primary-600">
                      {tool.price.toLocaleString('ro-RO')}€
                    </span>
                  </div>
                </div>

                {/* Usage Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Utilizare:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tool.usage}/{tool.maxUsage}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((tool.usage / tool.maxUsage) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedTool(tool);
                      setShowModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Detalii
                  </button>
                  <button className="flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tool Modal */}
      {showModal && (
        <ToolModal
          tool={selectedTool}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default InstallationTools;